import React, { useEffect, useState } from 'react';
import useClientPaging from '../../components/admin/useClientPaging';
import {
  Box, Typography, Chip, Paper, Button,
  Dialog, DialogTitle, DialogContent, Grid, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, MenuItem, Skeleton, Card, CardContent, Avatar,
  IconButton, Tooltip, DialogActions, CircularProgress
} from '@mui/material';
import { 
  Topic as TopicIcon, 
  VideoLibrary as VideoIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,

  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../api';

// Helper function to get media icon based on type
const getMediaIcon = (mediaType) => {
  if (mediaType?.startsWith('video/')) return <VideoIcon />;
  if (mediaType === 'application/pdf') return <PdfIcon />;
  if (mediaType?.startsWith('image/')) return <ImageIcon />;
  return <ImageIcon />; // Default fallback
};

// Helper function to get media color based on type
const getMediaColor = (mediaType) => {
  if (mediaType?.startsWith('video/')) return 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)';
  if (mediaType === 'application/pdf') return 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)';
  if (mediaType?.startsWith('image/')) return 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)';
  return 'linear-gradient(135deg, #9e9e9e 0%, #616161 100%)'; // Default gray
};

// Helper function to check if media can be displayed as image
const canDisplayAsImage = (mediaType) => {
  return mediaType?.startsWith('image/');
};
const gradientBg = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';

const gradientButtonStyle = {
  background: gradientBg,
  color: 'white',
  fontWeight: 600,
  px: 4,
  py: 1.5,
  borderRadius: 2,
  textTransform: 'none',
  boxShadow: '0 4px 15px rgba(102,126,234,0.3)',
  '&:hover': {
    background: 'linear-gradient(135deg, #5a6fd8, #6a42a0)',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(102,126,234,0.4)',
  },
  transition: 'all 0.25s ease',
};

const cardSx = {
  borderRadius: 3,
  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
  border: '1px solid rgba(255,255,255,0.8)',
  background: 'rgba(255,255,255,0.97)',
  backdropFilter: 'blur(20px)',
};


const TopicSubtopicList = () => {
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    course_id: '',
    subject_id: '',
    course_topic_id: '',
    course_subtopic_id: ''
  });

  const [dropdowns, setDropdowns] = useState({
    courses: [],
    subjects: [],
    topics: [],
    subtopics: []
  });

  const [data, setData] = useState([]);
  const { pageRows: pagedData, pager: dataPager } = useClientPaging(data);
  const [loading, setLoading] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [mediaViewer, setMediaViewer] = useState({ open: false, url: '', type: '', title: '' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, topic: null, isSubtopic: false });
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchTopicSubtopics(false);
    api.get('admin/master-form/courses-for-topics').then(res => {
      setDropdowns(prev => ({ ...prev, courses: res.data.data || [] }));
    });
  }, []);

  useEffect(() => {
    if (filters.course_id) {
      api.get(`admin/master-form/courses/${filters.course_id}/subjects`).then(res => {
        setDropdowns(prev => ({ ...prev, subjects: res.data.data || [], topics: [], subtopics: [] }));
      });
    } else {
      setDropdowns(prev => ({ ...prev, subjects: [], topics: [], subtopics: [] }));
    }
  }, [filters.course_id]);

  useEffect(() => {
    if (filters.subject_id) {
      const params = filters.course_id ? { course_id: filters.course_id } : {};
      api.get(`admin/fetch/course/topics-by-subject/${filters.subject_id}`, { params }).then(res => {
        setDropdowns(prev => ({ ...prev, topics: res.data.data || [], subtopics: [] }));
      });
    } else {
      setDropdowns(prev => ({ ...prev, topics: [], subtopics: [] }));
    }
  }, [filters.subject_id]);

  useEffect(() => {
    if (filters.course_topic_id) {
      api.get(`admin/fetch/course/subtopic/${filters.course_topic_id}`).then(res => {
        setDropdowns(prev => ({ ...prev, subtopics: res.data.data || [] }));
      });
    } else {
      setDropdowns(prev => ({ ...prev, subtopics: [] }));
    }
  }, [filters.course_topic_id]);

  const fetchTopicSubtopics = async (applyFilters = false) => {
    setLoading(true);
    try {
      const params = applyFilters
        ? Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== '' && v !== null && v !== undefined))
        : {};
      const res = await api.get('admin/assign/topic/subtopic', { params });
      setData(res.data.data?.data || []);
    } catch (err) {
      console.error('❌ Failed to fetch topic-subtopic list');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      ...(key === 'course_id' && { subject_id: '', course_topic_id: '', course_subtopic_id: '' }),
      ...(key === 'subject_id' && { course_topic_id: '', course_subtopic_id: '' }),
      ...(key === 'course_topic_id' && { course_subtopic_id: '' })
    }));
  };

  const openMediaViewer = (url, type, title) => {
    setMediaViewer({ open: true, url, type, title });
  };

  const closeMediaViewer = () => {
    setMediaViewer({ open: false, url: '', type: '', title: '' });
  };

  const handleEditTopic = (topic) => {
    const topicId = topic.topic_id || topic.id || topic.ID;
    if (!topicId) {
      console.error('Topic object missing ID field:', topic);
      toast.error('Cannot edit: Topic ID not found');
      return;
    }
    console.log('🔵 Edit topic clicked, ID:', topicId);
    navigate(`/admin/course-content/topic/${topicId}`);
  };

  const handleEditSubtopic = (subtopic, topicId) => {
    const subtopicId = subtopic.id || subtopic.subtopic_id || subtopic.ID;
    if (!subtopicId) {
      console.error('Subtopic object missing ID field:', subtopic);
      toast.error('Cannot edit: Subtopic ID not found');
      return;
    }
    console.log('🔵 Edit subtopic clicked, ID:', subtopicId);
    navigate(`/admin/course-content/subtopic/${subtopicId}`);
  };

  const handleDeleteClick = (item, isSubtopic = false) => {
    setDeleteDialog({ open: true, topic: item, isSubtopic });
  };

  const handleDeleteConfirm = async () => {
    const { topic, isSubtopic } = deleteDialog;
    if (!topic) return;

    const itemId = isSubtopic 
      ? (topic.id || topic.subtopic_id || topic.ID)
      : (topic.topic_id || topic.id || topic.ID);
    
    if (!itemId) {
      toast.error(`Cannot delete: ${isSubtopic ? 'Subtopic' : 'Topic'} ID not found`);
      setDeleteDialog({ open: false, topic: null, isSubtopic: false });
      return;
    }

    setDeletingId(itemId);
    console.log(`🗑️ Attempting to delete ${isSubtopic ? 'subtopic' : 'topic'} ID:`, itemId);
    
    try {
      const endpoint = isSubtopic
        ? `admin/assign/topic/subtopic/subtopic/${itemId}`
        : `admin/assign/topic/subtopic/${itemId}`;
      
      const response = await api.delete(endpoint);
      console.log('✅ Delete response:', response.data);
      
      if (response.data.success) {
        toast.success(response.data.message || `${isSubtopic ? 'Subtopic' : 'Topic'} deleted successfully`);
        setDeleteDialog({ open: false, topic: null, isSubtopic: false });
        fetchTopicSubtopics(true);
      }
    } catch (err) {
      console.error(`❌ Error deleting ${isSubtopic ? 'subtopic' : 'topic'}:`, err);
      if (err.response?.status === 422) {
        toast.error(err.response.data.error || `Cannot delete ${isSubtopic ? 'subtopic' : 'topic'}`);
      } else if (err.response?.status === 404) {
        toast.error(`${isSubtopic ? 'Subtopic' : 'Topic'} not found`);
        fetchTopicSubtopics(true);
      } else if (err.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else {
        toast.error(err.response?.data?.error || `Failed to delete ${isSubtopic ? 'subtopic' : 'topic'}`);
      }
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, topic: null, isSubtopic: false });
  };

  return (
    <Box sx={{ 
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      minHeight: '100vh',
      p: 3
    }}>
      {/* Header Section */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 4 
      }}>
        {/* Left Side - Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            width: 56,
            height: 56
          }}>
            <TopicIcon sx={{ fontSize: 28, color: 'white' }} />
          </Avatar>
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 0.5
            }}>
              Topic & Subtopic Media
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage course topics and their multimedia content
            </Typography>
          </Box>
        </Box>

        {/* Right Side - Add Button */}
        <Button
        
           sx={gradientButtonStyle} onClick={() => navigate('/admin/course-content')}>
                          + Add Content
                  </Button>
             
      </Box>

      {/* Filters Card */}
      <Card sx={{ mb: 4, ...cardSx }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <Avatar sx={{ width: 28, height: 28, background: gradientBg, flexShrink: 0 }}>
              <FilterIcon sx={{ fontSize: 16 }} />
            </Avatar>
            <Typography variant="subtitle1" fontWeight={600} color="#1a1a2e">
              Filter Topics & Subtopics
            </Typography>
            <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(102,126,234,0.15)' }} />
          </Box>

          <Grid container spacing={3}>
            {[
              { key: 'course_id', label: 'Course', options: dropdowns.courses, dependsOn: null },
              { key: 'subject_id', label: 'Subject', options: dropdowns.subjects, dependsOn: 'course_id' },
              { key: 'course_topic_id', label: 'Topic', options: dropdowns.topics, dependsOn: 'subject_id' },
              { key: 'course_subtopic_id', label: 'Subtopic', options: dropdowns.subtopics, dependsOn: 'course_topic_id' },
            ].map(({ key, label, options, dependsOn }) => (
              <Grid item xs={12} sm={6} md={3} key={key}>
                <TextField
                  select
                  fullWidth
                  label={label}
                  value={filters[key]}
                  onChange={(e) => handleFilterChange(key, e.target.value)}
                  disabled={dependsOn !== null && !filters[dependsOn]}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      background: 'white'
                    }
                  }}
                >
                  <MenuItem value=""><em>— All —</em></MenuItem>
                  {options.map(opt => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.name || opt.topic_name || opt.subject_name || opt.subtopic_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            ))}

            <Grid item xs={12} sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
              <Button
                onClick={() => fetchTopicSubtopics(true)}
                startIcon={<FilterIcon />}
                sx={gradientButtonStyle}
              >
                Apply Filters
              </Button>
              <Button
                onClick={() => {
                  setFilters({
                    course_id: '', subject_id: '', course_topic_id: '', course_subtopic_id: ''
                  });
                  fetchTopicSubtopics(false);
                }}
                startIcon={<RefreshIcon />}
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  borderColor: '#667eea',
                  color: '#667eea',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: '#764ba2',
                    background: 'rgba(102,126,234,0.06)',
                  },
                }}
              >
                Reset
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Results Card */}
      <Card sx={cardSx}>
        {loading ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
                  {['Topic ID', 'Topic Name', 'Course', 'Subject', 'Subtopic Count', 'Subtopic Details', 'Media', 'Actions'].map((text, i) => (
                    <TableCell key={i} sx={{ fontWeight: 600 }}>
                      <Skeleton width={100} />
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {[...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(8)].map((__, j) => (
                      <TableCell key={j}><Skeleton width="80%" /></TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : data.length === 0 ? (
          <Box sx={{ p: 8, textAlign: 'center' }}>
            <Avatar sx={{ 
              width: 80, 
              height: 80, 
              mx: 'auto', 
              mb: 2,
              background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)'
            }}>
              <TopicIcon sx={{ fontSize: 40, color: '#1976d2' }} />
            </Avatar>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Topics Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your filters or add some content
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
                  <TableCell sx={{ fontWeight: 600, color: '#495057' }}>Topic ID</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#495057' }}>Topic Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#495057' }}>Course</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#495057' }}>Subject</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#495057' }}>Subtopic Count</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#495057' }}>Subtopic Details</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#495057' }}>Media</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#495057' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedData.map((item, idx) => (
                  <TableRow 
                    key={idx}
                    sx={{
                      '&:hover': {
                        background: 'rgba(102, 126, 234, 0.05)',
                        transform: 'scale(1.01)',
                        transition: 'all 0.2s ease'
                      }
                    }}
                  >
                    <TableCell>
                      <Chip 
                        label={item.topic_id} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {item.topic_name}
                      </Typography>
                    </TableCell>
                    <TableCell>{item.course_name || "—"}</TableCell>
                    <TableCell>
                      <Chip 
                        label={item.subject_name} 
                        size="small" 
                        sx={{ background: '#fff3e0', color: '#f57c00' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={item.subtopic?.length || 0} 
                        size="small" 
                        sx={{ 
                          background: item.subtopic?.length > 0 ? '#e8f5e8' : '#ffebee',
                          color: item.subtopic?.length > 0 ? '#2e7d32' : '#c62828',
                          fontWeight: 600
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ maxWidth: 200 }}>
                        {item.subtopic?.length > 0 ? (
                          <Box>
                            {item.subtopic.slice(0, 2).map((sub, i) => (
                              <Chip
                                key={i}
                                label={sub.name}
                                size="small"
                                variant="outlined"
                                sx={{ 
                                  mr: 0.5, 
                                  mb: 0.5,
                                  fontSize: '10px',
                                  height: 20
                                }}
                              />
                            ))}
                            {item.subtopic.length > 2 && (
                              <Chip
                                label={`+${item.subtopic.length - 2} more`}
                                size="small"
                                sx={{ 
                                  background: '#e3f2fd',
                                  color: '#1976d2',
                                  fontSize: '10px',
                                  height: 20
                                }}
                              />
                            )}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            No subtopics
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {(() => {
                        // Check if there's any media in topic_media or subtopic media
                        const hasTopicMedia = item.topic_media?.length > 0;
                        const hasSubtopicMedia = item.subtopic?.some(sub => sub.media?.length > 0);
                        const hasAnyMedia = hasTopicMedia || hasSubtopicMedia;
                        
                        return hasAnyMedia ? (
                          <Button 
                            variant="contained" 
                            size="small" 
                            onClick={() => setSelectedTopic(item)}
                            sx={{
                              ...gradientButtonStyle,
                              px: 2,
                              py: 0.75,
                            }}
                          >
                            View Media
                          </Button>
                        ) : (
                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            No media
                          </Typography>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        <Tooltip title="Edit Topic">
                          <IconButton
                            size="medium"
                            color="primary"
                            onClick={() => handleEditTopic(item)}
                            sx={{
                              border: '1px solid',
                              borderColor: 'primary.main',
                              '&:hover': { 
                                backgroundColor: 'rgba(25, 118, 210, 0.1)',
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.2s'
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Topic">
                          <IconButton
                            size="medium"
                            color="error"
                            onClick={() => handleDeleteClick(item, false)}
                            disabled={deletingId === (item.topic_id || item.id || item.ID)}
                            sx={{
                              border: '1px solid',
                              borderColor: 'error.main',
                              '&:hover': { 
                                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.2s'
                            }}
                          >
                            {deletingId === (item.topic_id || item.id || item.ID) ? (
                              <CircularProgress size={20} />
                            ) : (
                              <DeleteIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {dataPager}
      </Card>

      {/* Enhanced Dialog for Subtopics */}
      <Dialog 
        open={!!selectedTopic} 
        onClose={() => setSelectedTopic(null)} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 3
        }}>
          <Avatar sx={{ background: 'rgba(255,255,255,0.2)' }}>
            <TopicIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Subtopics for: {selectedTopic?.topic_name}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {selectedTopic?.subtopic?.length || 0} subtopics found
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          {/* Topic Media Section */}
          {selectedTopic?.topic_media?.length > 0 && (
            <Card sx={{ 
              mb: 4, 
              borderRadius: 3,
              background: 'rgba(255,255,255,0.95)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              border: '2px solid rgba(102, 126, 234, 0.2)'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Avatar sx={{ 
                    background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                    width: 40,
                    height: 40
                  }}>
                    <TopicIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={600} sx={{ color: '#ff9800' }}>
                      Topic Media: {selectedTopic?.topic_name}
                    </Typography>
                    <Chip 
                      label={`${selectedTopic?.topic_media?.length} media files`} 
                      size="small" 
                      variant="outlined"
                      sx={{ mt: 0.5, borderColor: '#ff9800', color: '#ff9800' }}
                    />
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  {selectedTopic?.topic_media?.map((media, j) => (
                    <Grid item xs={12} sm={6} md={4} key={j}>
                      <Card sx={{ 
                        borderRadius: 2,
                        overflow: 'hidden',
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        '&:hover': {
                          transform: 'translateY(-8px) scale(1.02)',
                          boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                          '& .media-overlay': {
                            opacity: 1
                          }
                        }
                      }}>
                        <Box sx={{ position: 'relative' }}>
                          <Avatar
                            src={canDisplayAsImage(media.type) ? media.url : undefined}
                            sx={{ 
                              width: '100%', 
                              height: 150, 
                              borderRadius: 0,
                              background: getMediaColor(media.type)
                            }}
                            variant="square"
                          >
                            {getMediaIcon(media.type)}
                          </Avatar>
                          <Chip 
                            label={media.type?.split('/')[1]?.toUpperCase() || 'MEDIA'} 
                            size="small"
                            sx={{ 
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              background: 'rgba(0,0,0,0.7)',
                              color: 'white',
                              fontWeight: 600
                            }}
                          />
                          
                          {/* Fancy Hover Overlay */}
                          <Box 
                            className="media-overlay"
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: 'linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(102, 126, 234, 0.8) 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              opacity: 0,
                              transition: 'opacity 0.3s ease'
                            }}
                          >
                            <Button
                              variant="contained"
                              onClick={() => openMediaViewer(media.url, media.type, `${selectedTopic?.topic_name} - Topic Media`)}
                              sx={{
                                background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                                color: '#333',
                                fontWeight: 700,
                                px: 3,
                                py: 1.5,
                                borderRadius: 3,
                                boxShadow: '0 8px 25px rgba(0,0,0,0.3)',
                                textTransform: 'none',
                                fontSize: '14px',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                  color: 'white',
                                  transform: 'scale(1.1)',
                                  boxShadow: '0 12px 35px rgba(0,0,0,0.4)'
                                }
                              }}
                            >
                              {media.type?.startsWith('video/') ? '🎬 Play Video' :
                               media.type === 'application/pdf' ? '📄 View PDF' : '🖼️ View Image'}
                            </Button>
                          </Box>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Subtopic Media Sections */}
          {selectedTopic?.subtopic?.map((sub, i) => (
            <Card key={i} sx={{ 
              mb: 3, 
              borderRadius: 3,
              background: 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
            }}>
              <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ 
                        background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
                        width: 40,
                        height: 40
                      }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                          {i + 1}
                        </Typography>
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight={600}>
                          {sub.name}
                        </Typography>
                        <Chip 
                          label={`ID: ${sub.id}`} 
                          size="small" 
                          variant="outlined"
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Edit Subtopic">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditSubtopic(sub, selectedTopic?.topic_id)}
                          sx={{
                            border: '1px solid',
                            borderColor: 'primary.main',
                            '&:hover': { 
                              backgroundColor: 'rgba(25, 118, 210, 0.1)',
                              transform: 'scale(1.1)'
                            },
                            transition: 'all 0.2s'
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Subtopic">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(sub, true)}
                          disabled={deletingId === (sub.id || sub.subtopic_id || sub.ID)}
                          sx={{
                            border: '1px solid',
                            borderColor: 'error.main',
                            '&:hover': { 
                              backgroundColor: 'rgba(244, 67, 54, 0.1)',
                              transform: 'scale(1.1)'
                            },
                            transition: 'all 0.2s'
                          }}
                        >
                          {deletingId === (sub.id || sub.subtopic_id || sub.ID) ? (
                            <CircularProgress size={16} />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                <Grid container spacing={3}>
                  {sub.media?.length > 0 ? (
                    sub.media.map((media, j) => (
                      <Grid item xs={12} sm={6} md={4} key={j}>
                        <Card sx={{ 
                          borderRadius: 2,
                          overflow: 'hidden',
                          transition: 'all 0.3s ease',
                          position: 'relative',
                          '&:hover': {
                            transform: 'translateY(-8px) scale(1.02)',
                            boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                            '& .media-overlay': {
                              opacity: 1
                            }
                          }
                        }}>
                          <Box sx={{ position: 'relative' }}>
                            <Avatar
                              src={canDisplayAsImage(media.type) ? media.url : undefined}
                              sx={{ 
                                width: '100%', 
                                height: 150, 
                                borderRadius: 0,
                                background: getMediaColor(media.type)
                              }}
                              variant="square"
                            >
                              {getMediaIcon(media.type)}
                            </Avatar>
                            <Chip 
                              label={media.type?.split('/')[1]?.toUpperCase() || 'MEDIA'} 
                              size="small"
                              sx={{ 
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                background: 'rgba(0,0,0,0.7)',
                                color: 'white',
                                fontWeight: 600
                              }}
                            />
                            
                            {/* Fancy Hover Overlay */}
                            <Box 
                              className="media-overlay"
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(76, 175, 80, 0.8) 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: 0,
                                transition: 'opacity 0.3s ease'
                              }}
                            >
                              <Button
                                variant="contained"
                                onClick={() => openMediaViewer(media.url, media.type, `${sub.name} - Subtopic Media`)}
                                sx={{
                                  background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                                  color: '#333',
                                  fontWeight: 700,
                                  px: 3,
                                  py: 1.5,
                                  borderRadius: 3,
                                  boxShadow: '0 8px 25px rgba(0,0,0,0.3)',
                                  textTransform: 'none',
                                  fontSize: '14px',
                                  '&:hover': {
                                    background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
                                    color: 'white',
                                    transform: 'scale(1.1)',
                                    boxShadow: '0 12px 35px rgba(0,0,0,0.4)'
                                  }
                                }}
                              >
                                {media.type?.startsWith('video/') ? '🎬 Play Video' :
                                 media.type === 'application/pdf' ? '📄 View PDF' : '🖼️ View Image'}
                              </Button>
                            </Box>
                          </Box>
                        </Card>
                      </Grid>
                    ))
                  ) : (
                    <Grid item xs={12}>
                      <Box sx={{ 
                        textAlign: 'center', 
                        py: 4,
                        color: 'text.secondary'
                      }}>
                        <ImageIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                        <Typography variant="body2">
                          No media available for this subtopic
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          ))}
        </DialogContent>
      </Dialog>

      {/* Enhanced Media Viewer Modal */}
      <Dialog
        open={mediaViewer.open}
        onClose={closeMediaViewer}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            minHeight: '85vh',
            boxShadow: '0 25px 50px rgba(0,0,0,0.3)',
            border: '2px solid rgba(102, 126, 234, 0.2)'
          }
        }}
        TransitionProps={{
          timeout: 500
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ 
              background: 'rgba(255,255,255,0.2)',
              width: 40,
              height: 40
            }}>
              {getMediaIcon(mediaViewer.type)}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                {mediaViewer.title}
              </Typography>
              <Chip 
                label={mediaViewer.type?.split('/')[1]?.toUpperCase() || 'MEDIA'} 
                size="small"
                sx={{ 
                  background: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '10px'
                }}
              />
            </Box>
          </Box>
          <IconButton
            onClick={closeMediaViewer}
            sx={{ 
              color: 'white',
              background: 'rgba(255,255,255,0.1)',
              '&:hover': { 
                background: 'rgba(255,255,255,0.2)',
                transform: 'scale(1.1)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ 
          p: 2, 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          background: '#000',
          position: 'relative'
        }}>
          {mediaViewer.type?.startsWith('video/') ? (
            <Box sx={{ width: '100%', position: 'relative' }}>
              <Box sx={{ 
                position: 'absolute',
                top: 10,
                left: 10,
                zIndex: 10,
                background: 'rgba(0,0,0,0.7)',
                borderRadius: 2,
                px: 2,
                py: 1
              }}>
                <Typography variant="caption" sx={{ color: 'white', fontWeight: 600 }}>
                  🎬 Playing Video - {mediaViewer.type?.split('/')[1]?.toUpperCase()}
                </Typography>
              </Box>
              <video
                controls
                autoPlay
                style={{
                  width: '100%',
                  height: '75vh',
                  objectFit: 'contain',
                  borderRadius: '8px'
                }}
              >
                <source src={mediaViewer.url} type={mediaViewer.type} />
                Your browser does not support the video tag.
              </video>
            </Box>
          ) : mediaViewer.type === 'application/pdf' ? (
            <Box sx={{ width: '100%', height: '75vh', position: 'relative' }}>
              <Box sx={{ 
                position: 'absolute',
                top: 10,
                left: 10,
                zIndex: 10,
                background: 'rgba(244, 67, 54, 0.9)',
                borderRadius: 2,
                px: 2,
                py: 1
              }}>
                <Typography variant="caption" sx={{ color: 'white', fontWeight: 600 }}>
                  📄 PDF Document Viewer
                </Typography>
              </Box>
              <iframe
                src={mediaViewer.url}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  borderRadius: '8px',
                  background: 'white'
                }}
                title="PDF Viewer"
              />
            </Box>
          ) : mediaViewer.type?.startsWith('image/') ? (
            <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Box sx={{ 
                position: 'absolute',
                top: 10,
                left: 10,
                zIndex: 10,
                background: 'rgba(76, 175, 80, 0.9)',
                borderRadius: 2,
                px: 2,
                py: 1
              }}>
                <Typography variant="caption" sx={{ color: 'white', fontWeight: 600 }}>
                  🖼️ Image Viewer - {mediaViewer.type?.split('/')[1]?.toUpperCase()}
                </Typography>
              </Box>
              <img
                src={mediaViewer.url}
                alt="Media"
                style={{
                  maxWidth: '100%',
                  maxHeight: '75vh',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
                }}
              />
            </Box>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center', color: 'white' }}>
              <Avatar sx={{ 
                width: 80, 
                height: 80, 
                mx: 'auto', 
                mb: 2,
                background: 'rgba(255,255,255,0.1)'
              }}>
                {getMediaIcon(mediaViewer.type)}
              </Avatar>
              <Typography variant="h6" color="white" gutterBottom>
                Media format not supported for preview
              </Typography>
              <Button
                variant="contained"
                onClick={() => window.open(mediaViewer.url, '_blank')}
                sx={{ 
                  mt: 2,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                }}
              >
                Open in New Tab
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1.5,
          color: 'error.main',
          pb: 1
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: '50%',
            backgroundColor: 'error.light',
            color: 'error.main',
            fontSize: '20px'
          }}>
            ⚠️
          </Box>
          Confirm Delete
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
            Are you sure you want to delete this {deleteDialog.isSubtopic ? 'subtopic' : 'topic'}? This action cannot be undone.
          </Typography>
          {deleteDialog.topic && (
            <Box sx={{ 
              p: 2, 
              backgroundColor: 'grey.50', 
              borderRadius: 1, 
              border: '1px solid', 
              borderColor: 'grey.300' 
            }}>
              <Typography variant="subtitle1" fontWeight={600}>
                <strong>{deleteDialog.isSubtopic ? 'Subtopic' : 'Topic'}:</strong> {deleteDialog.topic.name || deleteDialog.topic.topic_name || deleteDialog.topic.subtopic_name || 'N/A'}
              </Typography>
              {(deleteDialog.topic.topic_id || deleteDialog.topic.id) && (
                <Typography variant="body2" color="text.secondary">
                  <strong>ID:</strong> {deleteDialog.topic.topic_id || deleteDialog.topic.id}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1, gap: 1.5 }}>
          <Button 
            onClick={handleDeleteCancel}
            variant="outlined"
            color="inherit"
            size="large"
            sx={{ 
              minWidth: 100,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            size="large"
            disabled={deletingId === (deleteDialog.topic?.topic_id || deleteDialog.topic?.id || deleteDialog.topic?.subtopic_id || deleteDialog.topic?.ID)}
            autoFocus
            sx={{ 
              minWidth: 100,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }}
          >
            {deletingId === (deleteDialog.topic?.topic_id || deleteDialog.topic?.id || deleteDialog.topic?.subtopic_id || deleteDialog.topic?.ID) ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: 'white' }} />
                Deleting...
              </Box>
            ) : (
              `Delete ${deleteDialog.isSubtopic ? 'Subtopic' : 'Topic'}`
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TopicSubtopicList;
